import React, { useEffect, useState } from "react";
import { api } from "../api";

function fmtTime(iso?: string | null): string {
  if (!iso) return "-";
  return String(iso).replace("T", " ").slice(0, 19);
}

interface GoldJob {
  job_name: string;
  schema?: string;
  run_date?: string;
  [extra: string]: any;
}

interface GoldJobDetailProps {
  job: GoldJob | null;
  onClose: () => void;
}

const GoldJobDetail: React.FC<GoldJobDetailProps> = ({ job, onClose }) => {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState("");
  const [deps, setDeps] = useState<any[] | null>(null);
  const [depsError, setDepsError] = useState("");

  useEffect(() => {
    if (!job) return;
    setData(null);
    setError("");
    setDeps(null);
    setDepsError("");
    api.goldJobDetail({ job_name: job.job_name, schema: job.schema, date: job.run_date }).then(({ data: d }) => {
      if (d && d.error) setError(d.error);
      else setData(d);
    });
    api.goldJobDependencies(job.job_name).then(({ data: d, error: err }) => {
      if (err) setDepsError(err);
      else setDeps((d && d.dependencies) || []);
    });
  }, [job]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!job) return null;

  const ref = (data && data.ref) || {};
  const todayRuns: any[] = (data && data.today_runs) || [];
  const history: any[] = (data && data.history) || [];

  const isOverridden = ref.is_overridden === true || ref.is_overridden === 1;
  const refFields: Array<[string, any]> = [
    ["Market", ref.market], ["GDO #", ref.gdo_num], ["GDO Type", ref.gdo_type], ["Active", ref.is_active],
    ["Exp Start", ref.expected_start
      ? <span>{ref.expected_start}{isOverridden ? <span style={{ color: "#f59e0b", fontSize: ".7rem", marginLeft: 4 }}>● override</span> : <span style={{ color: "#10b981", fontSize: ".7rem", marginLeft: 4 }}>● auto</span>}</span>
      : null],
    ["Exp End", ref.expected_end || null],
    ["Hist Avg Start", ref.hist_avg_start], ["Hist Avg End", ref.hist_avg_end],
    ["Hist Avg Duration", ref.hist_avg_duration_mins != null ? `${Math.round(ref.hist_avg_duration_mins)}m` : null],
    ["Hist Valid Runs (90d)", ref.hist_valid_runs], ["Hist Failed Runs (90d)", ref.hist_failed_runs],
    ["FS61 Start", ref.fs61_start], ["FS61 End", ref.fs61_end], ["Comment", ref.comment],
  ];

  return (
    <div className="detail-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="detail-panel">
        <div className="detail-header">
          <h3>{job.job_name}</h3>
          <button className="detail-close" onClick={onClose}>&times;</button>
        </div>
        <div className="detail-body">
          {error && <div className="detail-item"><span className="value" style={{ color: "#e74c3c" }}>{error}</span></div>}
          {!data && !error && <div className="detail-item"><span className="value">Loading...</span></div>}

          {data && (
            <>
              <div className="detail-section">
                <h4>Reference ({job.schema})</h4>
                <div className="detail-grid">
                  {refFields.map(([label, value]) => (
                    <div className="detail-kv" key={label}><span className="dk">{label}</span><span className="dv">{value ?? "-"}</span></div>
                  ))}
                </div>
              </div>

              <div className="detail-section">
                <h4>Today's Runs ({data.run_date})</h4>
                {todayRuns.length === 0 ? (
                  <div className="no-rows-msg">No runs found for this date</div>
                ) : (
                  <table className="detail-table">
                    <thead><tr><th>Status</th><th>Start</th><th>End</th><th>Duration</th><th>Trigger</th><th>Error</th></tr></thead>
                    <tbody>
                      {todayRuns.map((r: any, i: number) => (
                        <tr key={i}>
                          <td>{r.job_status || "-"}</td>
                          <td>{fmtTime(r.actual_start)}</td>
                          <td>{fmtTime(r.actual_end)}</td>
                          <td>{r.duration_mins != null ? `${Math.round(r.duration_mins)}m` : "-"}</td>
                          <td>{r.trigger_type || "-"}</td>
                          <td title={r.error_message || ""}>{(r.error_message || "-").slice(0, 50)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="detail-section">
                <h4>7-Day History</h4>
                {history.length === 0 ? (
                  <div className="no-rows-msg">No history yet</div>
                ) : (
                  <table className="detail-table">
                    <thead><tr><th>Date</th><th>Runs</th><th>OK</th><th>Failed</th><th>Manual</th><th>Avg Dur</th></tr></thead>
                    <tbody>
                      {history.map((h: any) => (
                        <tr key={h.run_date}>
                          <td>{h.run_date}</td>
                          <td>{h.run_count}</td>
                          <td className="det-ok">{h.successful}</td>
                          <td className={h.failed > 0 ? "det-fail" : ""}>{h.failed}</td>
                          <td className={h.manual_runs > 0 ? "det-manual" : ""}>{h.manual_runs || 0}</td>
                          <td>{h.avg_duration_mins != null ? `${Math.round(h.avg_duration_mins)}m` : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="detail-section">
                <h4>Pre-Dependencies</h4>
                {depsError ? (
                  <div className="no-rows-msg" style={{ color: "#e53935" }}>{depsError}</div>
                ) : !deps ? (
                  <div className="no-rows-msg">Loading&hellip;</div>
                ) : deps.length === 0 ? (
                  <div className="no-rows-msg">No pre-dependencies found</div>
                ) : (
                  <table className="detail-table">
                    <thead><tr><th>Market (Schema)</th><th>Trigger Target</th><th>Job Name</th><th>Mandatory</th><th>Status</th></tr></thead>
                    <tbody>
                      {deps.map((d: any, i: number) => (
                        <tr key={i}>
                          <td><code style={{ fontSize: ".75rem" }}>{d.schema_name || "-"}</code></td>
                          <td>{d.trigger_target_name || "-"}</td>
                          <td>{d.job_name || "-"}</td>
                          <td>{String(d.Is_mandatory || "").toUpperCase() === "T" ? <span className="det-ok" style={{ fontWeight: 600 }}>Yes</span> : <span style={{ color: "#64748b" }}>No</span>}</td>
                          <td>{String(d.Is_Active || "").toLowerCase() === "true" ? <span className="det-ok">Active</span> : <span className="det-fail">Inactive</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoldJobDetail;
